import { useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type { EditUser, ModelState } from 'BrokerTsLib/src/dtos'
import { BadRequestError } from 'BrokerTsLib/src/dtos'
import {
  useGetUser,
  useGetUserProperties,
  useUpdateUser,
} from 'BrokerTsLib/src/hooks/useUser'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Loading } from '@/components'
import { UserForm } from '@/containers/user/UserForm'
import { UserHeader } from '@/containers/user/UserHeader'
import { useAuth } from '@/context/AuthContext'
import { SidebarProvider } from '@/context/SidebarContext'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { BreadcrumbItems } from 'BrokerTsLib/src/containers/Breadcrumbs'
import { Breadcrumbs } from 'BrokerTsLib/src/containers/Breadcrumbs'

export const UserEditPage = () => {
  const { userId } = useParams()
  const { user } = useAuth()

  const {
    data,
    isLoading: isUserLoading,
    isRefetching: isUserRefetching,
  } = useGetUser(userId!)
  const {
    data: propertiesData,
    isLoading: isPropertiesLoading,
    isRefetching: isPropertiesRefetching,
  } = useGetUserProperties(userId!)

  const isLoading =
    isUserLoading ||
    isPropertiesLoading ||
    isUserRefetching ||
    isPropertiesRefetching
  const navigate = useNavigate()
  const toast = useToast()
  const { mutateAsync: updateUser } = useUpdateUser(userId!)

  const onSubmit = async (values: EditUser) => {
    const result = await updateUser(values)
      .then(() => {
        navigate(`/user/${userId}`)
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při vytváření uživatele',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })

        if (e.status === 400)
          return new BadRequestError(e.response?.data as ModelState<EditUser>)

        return e.cause as Error
      })
    return result
  }

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItems = []

    items.push({
      to: `/organization/${data?.data.organizationUnitId}`,
      label: data?.data.organizationUnitName ?? 'Organizace',
      current: !user?.allowDisplayOrganization,
    })

    items.push({
      to: `/user/${userId}`,
      label: data?.data
        ? data.data.firstName ? `${data.data.firstName} ${data.data.lastName}` : data.data.userName
        : 'Uživatel',
    })

    items.push({
      to: `/user/${userId}/edit`,
      label: 'Upravit',
      current: true,
    })

    return items
  }, [user, userId, data])

  return (
    <SidebarProvider>
      <PageWrapper
        header={
          <>
            <Breadcrumbs items={breadcrumbs} />
            <UserHeader user={data?.data} isLoading={isLoading} />
          </>
        }
        sidebar={<Sidebar />}
        mainProps={{
          sx: {
            pt: { base: 0, md: 8 },
            gap: 6,
            display: 'grid',
            alignContent: 'baseline',
          },
        }}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <UserForm
            user={data!.data}
            properties={propertiesData!.data}
            onSubmit={onSubmit}
          />
        )}
      </PageWrapper>
    </SidebarProvider>
  )
}
